import {
  Box,
  Center,
  chakra,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
} from "@chakra-ui/react";
import { useInfiniteScroll } from "ahooks";
import { observer } from "mobx-react-lite";
import { toJS } from "mobx";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { langStore, translate } from "@noli/business";

import GalleryList from "react-photo-gallery";
import GalleryItem from "./GalleryItem";
import GalleryDetail from "@/pages/Gallery/components/GalleryDetail";

import { getPromptList } from "@/pages/Gallery/api";
import { gallerySeed } from "@/pages/Gallery/components/Search";
import { GalleryItemType } from "@/stores/gallery";
import { editorGalleryStore } from "@/modules/editor/stores/gallery";
import { loginStore } from "@/modules/login/store";

import { genBlock } from "@/utils/bem";

import { ReactComponent as LinkOutSvg } from "@/assets/icons/link_out.svg";
import { ReactComponent as SearchSvg } from "@/assets/icons/search.svg";

import Icon from "@/components/Icon";
import { sensitiveWordCheck } from "@/utils/string";
import { Editor_Gallery_Words } from "@/utils/lang";
import { onAlbum, onFollow } from "@/pages/Gallery/hooks";
import { useOwnToast } from "@/utils/misc";

import getScrollBarProps from "@/styles/scrollBarProps";
import "./style.scss";
import { WithClassName } from "@/types/base";
import classNames from "classnames";

interface FormatPhotoItemType {
  src: string;
  width: number;
  height: number;
  key: string;
  original: string;
  likes?: number;
}
const PAGE_SIZE = 40;
const SORT = "sort_default";
// const AUTHOR = "author_all";
const block = genBlock("e-gallery");
const IEditorGallery: React.FC<WithClassName> = ({ className }) => {
  const [current, setCurrent] = useState<number>(0);
  const [detailVisible, setDetailVisible] = useState(false);
  const lang = langStore.tgt;

  // 为了找到合适的展示 loading 的时机
  const prevPhotosLength = useRef(0);
  const loadedCount = useRef(0);
  /**
   * 当前图片对应的搜索关键词
   * 因为不同的搜索可能会出现相同的图片，这样图片不会重新加载，loadedCount 计数会不准确
   * 这样做其实很绕，有风险，如果有更好的解决方法则尽快改掉～
   */
  const [searchKeyword, setSearchKeyword] = useState<string>();

  const {
    galleryData,
    setGalleryData,
    keywords,
    setKeywords,
    empty,
    setEmpty,
    loading,
    setLoading,
  } = editorGalleryStore;
  const { list: photos, page_size, page_index, sort, counter } = galleryData;

  const toast = useOwnToast();
  const galleryWrapperRef = React.useRef<HTMLDivElement>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);

  const {
    mutate,
    loadingMore,
    data: scrollData,
    cancel,
  } = useInfiniteScroll(
    (data) => {
      if (loadingMore) return Promise.resolve({ ...data, counter, list: [] });
      return loadMore();
    },
    {
      target: galleryWrapperRef,
      isNoMore: (data?: { list: GalleryItemType[]; counter: any }) => {
        return data?.counter && photos.length >= counter;
      },
      manual: true,
      threshold: 400,
      reloadDeps: [],
    },
  );

  useEffect(() => {
    setLoading(true);
    getGallery().finally(() => {
      setLoading(false);
    });

    return () => {
      cancel();
      setGalleryData({
        page_index: 1,
        list: [],
        counter: 0,
        page_size: 40,
        sort: "sort_default",
      });
    };
  }, []);

  const updatePhotosLength = (length: number) => {
    prevPhotosLength.current = length;
    if (loadedCount.current) loadedCount.current = 0;
  };

  const getGallery = (
    loadMore?: boolean,
    emptyKeywords?: boolean,
    pageIndex: number = page_index,
    clearEmpty = true,
  ): Promise<{
    list: GalleryItemType[];
    counter: number;
  }> => {
    updatePhotosLength(galleryData.list?.length ?? 0);
    const reqPageIndex = pageIndex ?? (!loadMore ? 1 : 2);

    return getPromptList({
      keywords: emptyKeywords ? "" : keywords ?? "",
      page_index: reqPageIndex,
      page_size: PAGE_SIZE,
      seed: gallerySeed(!loadMore),
      sort: SORT,
      // author: AUTHOR,
    })
      .then(async (_res) => {
        try {
          const res = await _res;
          if (res.code === 0) {
            return res.data;
          } else {
            return { list: [], counter: 0 };
          }
        } catch {
          setLoading(false);
          return { list: [], counter: 0 };
        }
      })
      .then(async ({ list, counter }: { list: GalleryItemType[]; counter: number }) => {
        try {
          if (!list || !counter) {
            setEmpty(true);
            console.log("再次请求");
            const { list: recommendList, counter: recommendCount } = await getGallery(
              loadMore,
              true,
              reqPageIndex,
              false,
            );
            return {
              list: recommendList ?? [],
              counter: recommendCount ?? 0,
            };
          } else {
            if (clearEmpty) {
              setEmpty(false);
            }
            const newList = pageIndex <= 1 ? list : [...photos, ...list];
            setGalleryData({
              list: newList,
              counter: counter ?? 0,
              page_size: page_size,
              page_index: pageIndex + 1,
              sort,
            });
            if (reqPageIndex === 1 && galleryWrapperRef.current) {
              mutate({ ...scrollData, list: toJS(galleryData.list), counter });
              galleryWrapperRef.current?.scrollTo(0, 0);
            }
            setSearchKeyword(emptyKeywords ? "" : keywords ?? "");
            return {
              list,
              counter,
            };
          }
        } catch {
          setLoading(false);
          return { list: galleryData.list ?? [], counter: galleryData.counter ?? 0 };
        }
      });
  };

  const loadMore = () => {
    console.log("loadMore: ");
    return getGallery(true, empty, page_index, false);
  };

  const formatPhotos: FormatPhotoItemType[] = useMemo(
    () =>
      photos?.map((item) => {
        return {
          src: item.image_url,
          width: item.width ?? 300,
          height: item.height ?? 300,
          key: item.image_id.toString(),
          img_id: item.image_id,
          original: item.original,
          user: item.user,
          likes: item.likes,
          is_like: item.is_like,
        };
      }),
    [photos],
  );

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const searchValue = (e.target as any).value;
    const check = async (searchValue?: string) => {
      return searchValue ? sensitiveWordCheck(searchValue) : Promise.resolve();
    };
    if (searchValue && searchValue === searchKeyword) return;

    setLoading(true);

    check(searchValue)
      .then(() => {
        setEmpty(false);
        setGalleryData({
          ...galleryData,
          page_index: 1,
        });
        getGallery(false, false, 1, false);
      })
      .catch((err) => {
        setLoading(false);
        if (searchRef.current) {
          searchRef.current.value = "";
          setKeywords("");
        }
        toast({
          title: translate(Editor_Gallery_Words["search-sensitive-toast"], lang),
          status: "warning",
          position: "top",
        });
      });
  };

  function handleSrcChange() {
    const refreshImgCount = Math.min(prevPhotosLength.current ?? 0, galleryData.list.length ?? 0);
    loadedCount.current += 1;
    if (loading && refreshImgCount <= loadedCount.current) {
      setLoading(false);
      loadedCount.current = 0;
    }
  }

  return (
    <Flex className={classNames(className, block())} color="#fff">
      <Flex mb="16px" align="center" pr="6px" gap="10px" w="100%">
        <InputGroup size="sm" h="30px">
          <InputLeftElement h="30px" pointerEvents="none">
            <Icon svg={SearchSvg} fontSize="16px" />
          </InputLeftElement>
          <Input
            ref={searchRef}
            variant="filled"
            rounded="full"
            h="30px"
            size="sm"
            fontSize="12px"
            color="#fff"
            bg="#4D4D4D"
            _hover={{ bg: "#565656" }}
            _placeholder={{ color: "inherit", opacity: 0.4 }}
            _focusVisible={{ borderColor: "brand.active" }}
            onChange={(e) => setKeywords((e.target as any).value)}
            onKeyDown={(e) => {
              if (e.keyCode === 13) {
                // 回车
                handleSearch(e);
              }
            }}
            placeholder={translate(Editor_Gallery_Words["search-placeholeder"], lang)}
          ></Input>
        </InputGroup>
        {loginStore.isGuest ? null : (
          <chakra.a href="/gallery" target="_blank">
            <Icon fontSize="26px" svg={LinkOutSvg} cursor="pointer" />
          </chakra.a>
        )}
      </Flex>
      {empty && (
        <Text
          mb="16px"
          fontSize="13px"
          style={{ background: "transparent", border: "none", justifyContent: "center" }}
        >
          {translate(Editor_Gallery_Words["not-found-recommend"], lang)}
        </Text>
      )}
      {photos.length >= 1 && (
        <Box
          className={block({ e: "gallery-wrapper" })}
          ref={galleryWrapperRef}
          {...getScrollBarProps({ autoMargin: false })}
          overflowY={loading ? "hidden" : "auto"}
        >
          {loading ? (
            <div className={block({ e: "loading-wrapper" })}>
              <img
                src="https://nolipix-web.oss-cn-hangzhou.aliyuncs.com/assets/creator/loading-list.gif"
                alt=""
              />
            </div>
          ) : null}
          <GalleryList
            columns={2}
            direction="column"
            photos={formatPhotos}
            margin={4}
            renderImage={(data) => {
              return (
                <GalleryItem
                  list={photos}
                  key={data.index}
                  setCurrent={setCurrent}
                  data={data}
                  setDetailVisible={setDetailVisible}
                  onSrcChange={() => handleSrcChange()}
                  keyword={searchKeyword}
                />
              );
            }}
          />
          {/* {list.length === total && (
            <p className={styles.empty}>{galleryLangRecords[lang]["End-of-the-Page"]}</p>
          )} */}
        </Box>
      )}
      {detailVisible && (
        <Center
          pos="fixed"
          zIndex={6}
          width="100vw"
          height="100vh"
          left={0}
          top={0}
          bg="rgba(0, 0, 0, 0.7)"
        >
          <GalleryDetail
            currentID={current}
            backgroundColor={"#27272A"}
            onClose={() => {
              // history.back();
              setDetailVisible(false);
            }}
            onAlbum={onAlbum}
            onFollow={onFollow}
          />
        </Center>
      )}
    </Flex>
  );
};
export const EditorGallery = chakra(observer(IEditorGallery));
